import axios from 'axios';

export const MAINTENANCE_STATUS_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vR6j7hwaA4wVlmhSA89dIdTbuqqQfNhyI2AAgvVVmA7MpHYl8ud-ZMA0RLtPugKkV6jFsuwF86xXAdK/pub?output=csv';
export const CONNECTION_ERROR_MESSAGE = 'Error connecting to the server, check your connection';

const STATUS_REQUEST_TIMEOUT_MS = 5000;
const STATUS_CACHE_MS = 30000;
const MAX_CSV_LENGTH = 100000;
const MAX_MESSAGE_LENGTH = 2000;

export interface IMaintenanceStatus {
  available: boolean;
  message: string;
}

interface ICachedStatus {
  fetchedAt: number;
  status?: IMaintenanceStatus;
}

let cachedStatus: ICachedStatus | undefined;
let pendingStatusRequest: Promise<IMaintenanceStatus | undefined> | undefined;

/**
 * Returns the message to show after a backend connection failure.
 * A healthy status keeps the error that the failed operation already produced;
 * an unavailable status replaces it with the maintenance message.
 */
export async function getConnectionErrorMessage(currentErrorMessage: string): Promise<string> {
  const status = await getCurrentMaintenanceStatus();
  return getConnectionErrorMessageForStatus(currentErrorMessage, status);
}

export function getConnectionErrorMessageForStatus(
  currentErrorMessage: string,
  status?: IMaintenanceStatus,
): string {
  if (!status) {
    return CONNECTION_ERROR_MESSAGE;
  }

  return status.available ? currentErrorMessage : status.message;
}

export function isNetworkFailureOrTimeout(error: any): boolean {
  if (!error || error.response) {
    return false;
  }

  if (error.request || error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return true;
  }

  return typeof error.message === 'string' &&
    /(network error|network request failed|failed to fetch|load failed|timed?\s*out|timeout)/i.test(error.message);
}

export function isWebSocketNetworkFailureCode(closeCode: number): boolean {
  return closeCode === 1006;
}

export function parseMaintenanceStatusCsv(value: unknown): IMaintenanceStatus | undefined {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_CSV_LENGTH) {
    return undefined;
  }

  const rows = parseCsv(value.replace(/^\uFEFF/, ''));
  if (!rows || rows.length < 2) {
    return undefined;
  }

  const headers = rows[0].map(header => header.trim().toLowerCase());
  const availableIndex = headers.indexOf('available');
  const messageIndex = headers.indexOf('message');

  if (availableIndex < 0 || messageIndex < 0 ||
      headers.filter(header => header === 'available').length !== 1 ||
      headers.filter(header => header === 'message').length !== 1) {
    return undefined;
  }

  const dataRows = rows.slice(1).filter(row => row.some(cell => cell.trim().length > 0));
  if (dataRows.length === 0) {
    return undefined;
  }

  const currentRow = dataRows[dataRows.length - 1];
  if (availableIndex >= currentRow.length || messageIndex >= currentRow.length) {
    return undefined;
  }

  const availableValue = currentRow[availableIndex].trim().toLowerCase();
  if (availableValue !== 'true' && availableValue !== 'false') {
    return undefined;
  }

  const message = currentRow[messageIndex].trim();
  if (!message || message.length > MAX_MESSAGE_LENGTH || /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/.test(message)) {
    return undefined;
  }

  return {
    available: availableValue === 'true',
    message,
  };
}

async function getCurrentMaintenanceStatus(): Promise<IMaintenanceStatus | undefined> {
  const now = Date.now();
  if (cachedStatus && now - cachedStatus.fetchedAt < STATUS_CACHE_MS) {
    return cachedStatus.status;
  }

  if (pendingStatusRequest) {
    return pendingStatusRequest;
  }

  pendingStatusRequest = fetchCurrentMaintenanceStatus();
  let status: IMaintenanceStatus | undefined;
  try {
    status = await pendingStatusRequest;
  } catch (error) {
    // A failed lookup is cached briefly as well, otherwise websocket retries can
    // create a tight loop of requests while the device is offline.
    status = undefined;
  }

  cachedStatus = { fetchedAt: Date.now(), status };
  pendingStatusRequest = undefined;
  return status;
}

async function fetchCurrentMaintenanceStatus(): Promise<IMaintenanceStatus | undefined> {
  const response = await axios.get(MAINTENANCE_STATUS_URL, {
    params: { _: Date.now() },
    responseType: 'text',
    timeout: STATUS_REQUEST_TIMEOUT_MS,
  });

  return parseMaintenanceStatusCsv(response.data);
}

function parseCsv(value: string): string[][] | undefined {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let justClosedQuote = false;

  for (let index = 0; index < value.length; index++) {
    const character = value[index];

    if (quoted) {
      if (character === '"') {
        if (value[index + 1] === '"') {
          field += '"';
          index++;
        } else {
          quoted = false;
          justClosedQuote = true;
        }
      } else {
        field += character;
      }
      continue;
    }

    if (character === '"') {
      // Quotes are only valid at the beginning of a CSV field.
      if (field.length > 0 || justClosedQuote) {
        return undefined;
      }
      quoted = true;
    } else if (character === ',') {
      row.push(field);
      field = '';
      justClosedQuote = false;
    } else if (character === '\n' || character === '\r') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      justClosedQuote = false;
      if (character === '\r' && value[index + 1] === '\n') {
        index++;
      }
    } else if (justClosedQuote) {
      // Anything between a closing quote and the next delimiter is malformed.
      return undefined;
    } else {
      field += character;
    }
  }

  if (quoted) {
    return undefined;
  }

  row.push(field);
  rows.push(row);
  return rows;
}



// WEBPACK FOOTER //
// ./src/services/status/maintenance-status.ts